import { useState, useMemo } from 'react'
import { buildStarterSlots as buildSlots } from '../../lib/rosterSlots'
import { useNflDfsLive, useFantasyMatchupLive } from '../../hooks/useLeagues'
import { useAuth } from '../../hooks/useAuth'
import { useReadState, useMarkRead, READ_KINDS } from '../../hooks/useReadState'
import Avatar from '../ui/Avatar'
import UserProfileModal from '../profile/UserProfileModal'
import LoadingSpinner from '../ui/LoadingSpinner'
import { SkeletonCard } from '../ui/Skeleton'
import PlayerDetailModal from './PlayerDetailModal'
import InjuryBadge from '../ui/InjuryBadge'
// Three hand-rolled injury pills lived in this file. They painted a filled
// yellow/red background (the shared badge is colored text, no pill), matched
// statuses case-sensitively, and — worse — fell through to 'O' for anything
// unrecognised, so PUP, SUS and NA all rendered as "Out". Replaced with the
// shared component, which is case-insensitive and labels each status.

// Salary-cap NFL DFS lineup slots — fixed shape, not driven by roster_slots.
const SLOT_LABELS = { QB: 'QB', RB1: 'RB', RB2: 'RB', WR1: 'WR', WR2: 'WR', WR3: 'WR', TE: 'TE', FLEX: 'FLX', DEF: 'DEF' }
const SLOT_ORDER = ['QB', 'RB1', 'RB2', 'WR1', 'WR2', 'WR3', 'TE', 'FLEX', 'DEF']

// Traditional H2H starter slots are dynamic — build them from the league's
// roster_slots config so a wr=2 league doesn't show a phantom wr3.
// Shared definition — lib/rosterSlots. Compact labels, matching the
// matchup grid this view sits beside.
function buildH2HSlotMeta(rosterSlots) {
  const slots = buildSlots(rosterSlots)
  const labels = {}
  for (const sl of slots) labels[sl.key] = sl.shortLabel
  const order = slots.map((sl) => sl.key)
  return { starterSet: new Set(order), slotLabels: labels, slotOrder: order }
}

const IDP_STAT_POSITIONS = new Set([
  'DL', 'DE', 'DT', 'NT', 'LB', 'ILB', 'OLB', 'MLB', 'DB', 'CB', 'S', 'FS', 'SS',
])

// Tackles, sacks and the rest, in the order a box score reads them. Defenders
// score in every preset, so a defender with points and no stat line looked
// like the number came from nowhere.
function idpStatParts(stats) {
  const parts = []
  // Defensive TD first — biggest single play an IDP can make. Sleeper sends
  // it as idp_def_td; the sync folds it into def_td (see sleeperService.js).
  if (stats.def_td) parts.push(`${stats.def_td} TD`)
  if (stats.idp_tkl_solo) parts.push(`${stats.idp_tkl_solo} Tkl`)
  if (stats.idp_tkl_ast) parts.push(`${stats.idp_tkl_ast} Ast`)
  if (stats.idp_tkl_loss) parts.push(`${stats.idp_tkl_loss} TFL`)
  if (stats.idp_sack) parts.push(`${stats.idp_sack} Sk`)
  if (stats.idp_int) parts.push(`${stats.idp_int} INT`)
  if (stats.idp_pass_def) parts.push(`${stats.idp_pass_def} PD`)
  if (stats.idp_ff) parts.push(`${stats.idp_ff} FF`)
  if (stats.idp_fum_rec) parts.push(`${stats.idp_fum_rec} FR`)
  return parts
}

function buildStatLine(stats, position) {
  if (!stats) return null
  const parts = []
  if (position === 'QB') {
    if (stats.pass_yds) parts.push(`${stats.pass_yds} PaYD`)
    if (stats.pass_td) parts.push(`${stats.pass_td} PaTD`)
    if (stats.int) parts.push(`${stats.int} INT`)
    if (stats.rush_yds) parts.push(`${stats.rush_yds} RuYD`)
    if (stats.rush_td) parts.push(`${stats.rush_td} RuTD`)
  } else if (position === 'K') {
    if (stats.fgm) parts.push(`${stats.fgm} FG`)
    if (stats.fgm_50_plus) parts.push(`${stats.fgm_50_plus} 50+`)
    // Misses cost points, so they belong on the line — without them a
    // kicker's total reads higher than his stats explain.
    if (stats.fgmiss) parts.push(`${stats.fgmiss} Miss`)
    if (stats.xpm) parts.push(`${stats.xpm} XP`)
  } else if (position === 'DEF') {
    if (stats.def_sack) parts.push(`${stats.def_sack} SK`)
    if (stats.def_int) parts.push(`${stats.def_int} INT`)
    if (stats.def_fum_rec) parts.push(`${stats.def_fum_rec} FR`)
    if (stats.def_td) parts.push(`${stats.def_td} TD`)
    if (stats.def_safety) parts.push(`${stats.def_safety} SAF`)
    if (stats.def_pts_allowed != null) parts.push(`${stats.def_pts_allowed} PA`)
  } else if (IDP_STAT_POSITIONS.has(position)) {
    parts.push(...idpStatParts(stats))
  } else {
    if (stats.rush_yds) parts.push(`${stats.rush_yds} RuYD`)
    if (stats.rush_td) parts.push(`${stats.rush_td} RuTD`)
    if (stats.rec) parts.push(`${stats.rec} REC`)
    if (stats.rec_yds) parts.push(`${stats.rec_yds} ReYD`)
    if (stats.rec_td) parts.push(`${stats.rec_td} ReTD`)
    if (stats.fum) parts.push(`${stats.fum} FUM`)
  }
  // Return yardage applies at every position, so it sits outside the branches.
  if (!IDP_STAT_POSITIONS.has(position)) parts.push(...idpStatParts(stats))
  if (stats.ret_yds) parts.push(`${stats.ret_yds} RetYD`)
  if (stats.idp_int_ret_yd) parts.push(`${stats.idp_int_ret_yd} IntRetYD`)
  return parts.length > 0 ? parts.join(' \u00b7 ') : null
}

function formatNflPeriod(p) {
  if (!p) return ''
  if (p <= 4) return `Q${p}`
  if (p === 5) return 'OT'
  return `${p - 4}OT`
}

function gameClockLabel(slot) {
  if (slot.game_status === 'final') return 'Final'
  if (slot.game_status === 'live') {
    const period = formatNflPeriod(slot.game_period)
    const clock = slot.game_clock ? slot.game_clock : ''
    return [period, clock].filter(Boolean).join(' ') || 'Live'
  }
  return null
}

function SlotBorder({ status }) {
  if (status === 'live') return 'border-l-accent'
  if (status === 'final') return 'border-l-correct'
  return 'border-l-text-primary/20'
}

/** Salary cap DFS leaderboard with expandable rosters */
function SalaryCapLive({ league, week, season }) {
  const { profile } = useAuth()
  // Week being viewed. null = follow the league's current week, so a rollover
  // mid-session moves the view along instead of pinning it to a stale number.
  const [pickedWeek, setPickedWeek] = useState(null)
  const viewWeek = pickedWeek ?? week
  const isHistorical = viewWeek < week
  const canGoBack = viewWeek > 1
  const canGoForward = viewWeek < week
  const { data: liveData, isLoading } = useNflDfsLive(league.id, viewWeek, season, isHistorical)
  const [expandedUserId, setExpandedUserId] = useState(null)
  const [profileUserId, setProfileUserId] = useState(null)
  const [detailPlayerId, setDetailPlayerId] = useState(null)

  if (isLoading) return (
    <div className="space-y-3">
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  )

  const { members, all_final } = liveData || {}

  const weekNav = (
    <div className="flex items-center justify-between mb-4">
      <button
        onClick={() => { if (canGoBack) setPickedWeek(viewWeek - 1) }}
        disabled={!canGoBack}
        aria-label="Previous week"
        className="p-2 rounded-lg text-text-muted hover:text-text-primary transition-colors disabled:opacity-20"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
      </button>
      <span className="text-sm font-semibold text-text-primary">
        Week {viewWeek}
        {isHistorical && <span className="ml-2 text-xs font-normal text-text-muted">Final</span>}
      </span>
      <button
        onClick={() => { if (canGoForward) setPickedWeek(viewWeek + 1) }}
        disabled={!canGoForward}
        aria-label="Next week"
        className="p-2 rounded-lg text-text-muted hover:text-text-primary transition-colors disabled:opacity-20"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
      </button>
    </div>
  )

  if (!members?.length) {
    // Nav still renders, otherwise landing on an empty week traps you there
    // with no way back to a week that has rosters.
    return (
      <div>
        {weekNav}
        <div className="text-center py-8 text-sm text-text-secondary">No rosters for this week.</div>
      </div>
    )
  }

  return (
    <div>
      {weekNav}
      <div className="space-y-3">
      {members.map((m, idx) => {
        const isMe = m.user_id === profile?.id
        const isWinner = all_final && idx === 0
        const isExpanded = expandedUserId === m.user_id
        const borderColor = m.status === 'final' ? 'border-correct/50' : m.status === 'live' ? 'border-accent/50' : 'border-text-primary/20'

        // Slot status counters for the subtle "yet to play / playing / done" line
        const slots = m.slots || []
        const yetToPlay = slots.filter((s) => s.game_status === 'upcoming').length
        const playing = slots.filter((s) => s.game_status === 'live').length
        const done = slots.filter((s) => s.game_status === 'final').length

        return (
          <div key={m.user_id}>
            {/* Row is a div, not a button: the avatar inside is its own button
                (tap through to the profile) and nesting a button in a button
                is invalid HTML — browsers drop or reparent the inner one.
                role/tabIndex/onKeyDown keep it keyboard-operable. */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => setExpandedUserId(isExpanded ? null : m.user_id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setExpandedUserId(isExpanded ? null : m.user_id)
                }
              }}
              className={`w-full cursor-pointer rounded-xl border ${borderColor} bg-bg-primary transition-all text-left ${isWinner ? 'p-5' : 'p-4'}`}
            >
              <div className="flex items-center gap-3">
                {/* stopPropagation so tapping the avatar opens the profile
                    instead of also toggling the roster underneath. */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setProfileUserId(m.user_id) }}
                  className="shrink-0 rounded-full hover:opacity-80 active:scale-95 transition"
                  aria-label={`View ${m.user?.display_name || m.user?.username || 'user'}'s profile`}
                >
                  <Avatar user={m.user} size={isWinner ? 'xl' : 'lg'} />
                </button>
                <div className="flex-1 min-w-0">
                  <span className={`font-bold truncate ${isWinner ? 'text-lg text-accent' : isMe ? 'text-accent text-base' : 'text-text-primary text-base'}`}>
                    {m.user?.display_name || m.user?.username}
                  </span>
                  {m.has_roster && slots.length > 0 && (yetToPlay > 0 || playing > 0) && (
                    <div className="text-[10px] text-text-muted mt-0.5">
                      {playing > 0 && <span className="text-accent">{playing} playing</span>}
                      {playing > 0 && yetToPlay > 0 && <span> · </span>}
                      {yetToPlay > 0 && <span>{yetToPlay} yet to play</span>}
                      {done > 0 && (playing > 0 || yetToPlay > 0) && <span> · </span>}
                      {done > 0 && <span>{done} done</span>}
                    </div>
                  )}
                  {!m.has_roster && <div className="text-xs text-text-muted">No roster submitted</div>}
                </div>
                <span className={`font-display ${all_final && idx === 0 ? 'text-2xl' : 'text-xl'} text-white`}>
                  {Math.round(m.total_points * 10) / 10}
                </span>
                <svg className={`w-4 h-4 text-text-muted transition-transform shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {isExpanded && m.slots?.length > 0 && (
              <div className="mt-1 rounded-xl border border-text-primary/10 overflow-hidden">
                {[...m.slots].sort((a, b) => SLOT_ORDER.indexOf(a.roster_slot) - SLOT_ORDER.indexOf(b.roster_slot)).map((slot) => {
                  const hidden = slot.player_name === '????'
                  const slotBorder = SlotBorder({ status: slot.game_status })
                  const statLine = buildStatLine(slot.stats, slot.position)

                  return (
                    <div key={slot.roster_slot} className={`flex items-center gap-3 px-4 py-3.5 border-b border-text-primary/10 border-l-2 ${slotBorder} bg-bg-primary/30`}>
                      <span className="text-sm font-bold text-text-muted w-8 shrink-0">{SLOT_LABELS[slot.roster_slot] || slot.roster_slot}</span>
                      {hidden ? (
                        <span className="flex-1 text-base text-text-muted font-mono">????</span>
                      ) : (
                        <>
                          {slot.headshot_url && (
                            <img
                              src={slot.headshot_url}
                              alt=""
                              className="w-11 h-11 rounded-full object-cover bg-bg-secondary shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                              loading="eager"
                              decoding="async"
                              onClick={() => slot.player_id && setDetailPlayerId(slot.player_id)}
                              onError={(e) => { e.target.style.display = 'none' }}
                            />
                          )}
                          <div className="flex-1 min-w-0 lg:flex lg:items-center lg:gap-6">
                            <div
                              className="lg:w-44 lg:shrink-0 flex items-center gap-1.5 cursor-pointer"
                              onClick={() => slot.player_id && setDetailPlayerId(slot.player_id)}
                            >
                              <span className="text-base font-bold text-text-primary truncate hover:text-accent transition-colors">{slot.player_name}</span>
                              <InjuryBadge status={slot.injury_status} />
                            </div>
                            {statLine && (
                              <span className="text-xs text-text-muted block lg:hidden">{statLine}</span>
                            )}
                            <span className="text-sm text-text-secondary hidden lg:block lg:flex-1">{statLine || ''}</span>
                            {slot.away_team && slot.home_team && (
                              <span className="text-[11px] text-text-muted block mt-0.5 lg:mt-0 lg:text-xs lg:w-44 lg:shrink-0 lg:text-right">
                                {slot.away_team}
                                {(slot.game_status === 'live' || slot.game_status === 'final') && ` ${slot.away_score ?? ''}`}
                                {' @ '}
                                {slot.home_team}
                                {(slot.game_status === 'live' || slot.game_status === 'final') && ` ${slot.home_score ?? ''}`}
                                {slot.game_status === 'live' && slot.game_period && (
                                  <span className="text-text-primary ml-1.5">{formatNflPeriod(slot.game_period)} {slot.game_clock}</span>
                                )}
                                {slot.game_status === 'final' && (
                                  <span className="text-text-primary ml-1.5">Final</span>
                                )}
                              </span>
                            )}
                          </div>
                          {(slot.game_status === 'live' || slot.game_status === 'final') && (
                            <div className="flex flex-col items-end shrink-0 lg:ml-6 lg:w-16 lg:text-right">
                              <span className="text-base lg:text-lg font-display text-white">
                                {Math.round((slot.points_earned || 0) * 10) / 10}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
      </div>
      {detailPlayerId && (
        <PlayerDetailModal leagueId={league.id} playerId={detailPlayerId} onClose={() => setDetailPlayerId(null)} />
      )}
      {profileUserId && (
        <UserProfileModal userId={profileUserId} onClose={() => setProfileUserId(null)} />
      )}
    </div>
  )
}

/** Traditional H2H matchup live view */
function MatchupLive({ league, week, season, fantasySettings }) {
  const { profile } = useAuth()
  const { data, isLoading } = useFantasyMatchupLive(league.id, week, season)
  const [expandedMatchup, setExpandedMatchup] = useState(null)
  const [detailPlayerId, setDetailPlayerId] = useState(null)
  const { starterSet: H2H_STARTER_SET, slotLabels: H2H_SLOT_LABELS } = useMemo(
    () => buildH2HSlotMeta(fantasySettings?.roster_slots),
    [fantasySettings?.roster_slots]
  )

  if (isLoading) return (
    <div className="space-y-3">
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  )

  const matchups = data?.matchups || []

  if (!matchups.length) {
    return <div className="text-center py-8 text-sm text-text-secondary">No matchups for this week.</div>
  }

  // Find the user's matchup and put it first
  const sorted = [...matchups].sort((a, b) => {
    const aIsMe = a.home_user?.id === profile?.id || a.away_user?.id === profile?.id
    const bIsMe = b.home_user?.id === profile?.id || b.away_user?.id === profile?.id
    if (aIsMe && !bIsMe) return -1
    if (bIsMe && !aIsMe) return 1
    return 0
  })

  // My completed matchup result banner — dismiss once seen
  const myMatchup = matchups.find((m) => m.status === 'completed' && (m.home_user?.id === profile?.id || m.away_user?.id === profile?.id))
  // See FantasyMatchup for why this is derived rather than lazy state.
  const readState = useReadState()
  const markReadFn = useMarkRead()
  const [justDismissed, setJustDismissed] = useState(false)
  const resultDismissed = justDismissed
    || (!!myMatchup && readState[READ_KINDS.MATCHUP_RESULT]?.[myMatchup.id] === '1')
  const myResult = !resultDismissed && myMatchup ? (() => {
    const isHome = myMatchup.home_user?.id === profile?.id
    const myPts = isHome ? myMatchup.home_points : myMatchup.away_points
    const oppPts = isHome ? myMatchup.away_points : myMatchup.home_points
    const opponent = isHome ? myMatchup.away_user : myMatchup.home_user
    const won = myPts > oppPts
    const tied = myPts === oppPts
    return { won, tied, myPts, oppPts, opponent }
  })() : null

  return (
    <div className="space-y-4">
      {/* Weekly matchup result banner — dismissible */}
      {myResult && (
        <div className={`relative rounded-xl border p-4 text-center ${
          myResult.won ? 'border-correct/40 bg-correct/10' : myResult.tied ? 'border-accent/40 bg-accent/10' : 'border-incorrect/40 bg-incorrect/10'
        }`}>
          <button
            onClick={() => { markReadFn(READ_KINDS.MATCHUP_RESULT, myMatchup.id, '1'); setJustDismissed(true) }}
            className="absolute top-2 right-2 text-text-muted hover:text-text-primary text-lg leading-none"
          >&times;</button>
          <div className={`font-display text-lg ${myResult.won ? 'text-correct' : myResult.tied ? 'text-accent' : 'text-incorrect'}`}>
            {myResult.won ? 'Victory!' : myResult.tied ? 'Tie Game' : 'Defeat'}
          </div>
          <div className="flex items-center justify-center gap-4 mt-2">
            <div className="text-right">
              <div className="font-display text-2xl text-text-primary">{myResult.myPts}</div>
              <div className="text-[10px] text-text-muted">You</div>
            </div>
            <div className="text-text-muted text-sm">vs</div>
            <div className="text-left">
              <div className="font-display text-2xl text-text-secondary">{myResult.oppPts}</div>
              <div className="text-[10px] text-text-muted">{myResult.opponent?.display_name || myResult.opponent?.username || 'Opponent'}</div>
            </div>
          </div>
          <div className="text-xs text-text-muted mt-2">Week {week}</div>
        </div>
      )}

      {sorted.map((matchup) => {
        const isMyMatchup = matchup.home_user?.id === profile?.id || matchup.away_user?.id === profile?.id
        const isExpanded = expandedMatchup === matchup.id
        const homeWinning = matchup.home_points >= matchup.away_points

        const isCompleted = matchup.status === 'completed'
        const homeProb = isCompleted ? (homeWinning ? 100 : 0) : (matchup.home_win_prob ?? 50)
        const awayProb = isCompleted ? (!homeWinning ? 100 : 0) : (matchup.away_win_prob ?? 50)

        return (
          <div key={matchup.id} className="rounded-xl border border-text-primary/20 overflow-hidden bg-bg-primary">
            {/* Matchup header — tappable */}
            <button
              onClick={() => setExpandedMatchup(isExpanded ? null : matchup.id)}
              className="w-full p-4 text-left"
            >
              {/* Win probability bar */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-bold ${homeProb >= 50 ? 'text-correct' : 'text-text-muted'}`}>{homeProb}%</span>
                  <span className="text-xs text-text-muted uppercase tracking-wider">Win Prob</span>
                  <span className={`text-xs font-bold ${awayProb > 50 ? 'text-correct' : 'text-text-muted'}`}>{awayProb}%</span>
                </div>
                <div className="flex h-2 rounded-full overflow-hidden bg-text-primary/10">
                  <div
                    className={`transition-all duration-500 ${homeProb >= 50 ? 'bg-correct' : 'bg-text-muted/40'}`}
                    style={{ width: `${homeProb}%` }}
                  />
                  <div
                    className={`transition-all duration-500 ${awayProb > 50 ? 'bg-correct' : 'bg-text-muted/40'}`}
                    style={{ width: `${awayProb}%` }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Avatar user={matchup.home_user} size="lg" />
                  <div className="min-w-0">
                    <span className={`text-base font-bold truncate block ${homeWinning ? 'text-text-primary' : 'text-text-muted'}`}>
                      {matchup.home_user?.display_name || matchup.home_user?.username}
                    </span>
                    {matchup.home_projected > 0 && (
                      <span className="text-xs text-text-primary">Proj: {matchup.home_projected.toFixed(1)}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 px-4 shrink-0">
                  <span className={`font-display text-2xl ${homeWinning ? 'text-white' : 'text-text-muted'}`}>
                    {matchup.home_points.toFixed(1)}
                  </span>
                  <span className="text-sm text-text-muted">vs</span>
                  <span className={`font-display text-2xl ${!homeWinning ? 'text-white' : 'text-text-muted'}`}>
                    {matchup.away_points.toFixed(1)}
                  </span>
                </div>
                <div className="flex items-center gap-3 flex-1 min-w-0 justify-end">
                  <div className="min-w-0 text-right">
                    <span className={`text-base font-bold truncate block ${!homeWinning ? 'text-text-primary' : 'text-text-muted'}`}>
                      {matchup.away_user?.display_name || matchup.away_user?.username}
                    </span>
                    {matchup.away_projected > 0 && (
                      <span className="text-xs text-text-primary">Proj: {matchup.away_projected.toFixed(1)}</span>
                    )}
                  </div>
                  <Avatar user={matchup.away_user} size="md" />
                </div>
              </div>
            </button>

            {/* Expanded rosters */}
            {isExpanded && (
              <div className="border-t border-text-primary/10">
                <div className="grid grid-cols-2 divide-x divide-text-primary/10">
                  {/* Home roster */}
                  <div>
                    {matchup.home_roster.filter((s) => H2H_STARTER_SET.has(s.slot)).map((slot) => {
                      const statLine = buildStatLine(slot.stats, slot.position)
                      const slotBorder = slot.game_status === 'live' ? 'border-l-accent' : slot.game_status === 'final' ? 'border-l-correct' : 'border-l-text-primary/20'
                      return (
                        <div key={slot.slot} className={`flex items-center gap-3 px-4 py-3 border-b border-text-primary/5 border-l-2 ${slotBorder}`}>
                          <span className="text-xs font-bold text-text-muted w-7 shrink-0">{H2H_SLOT_LABELS[slot.slot] || slot.slot.toUpperCase()}</span>
                          {slot.headshot_url && (
                            <img
                              src={slot.headshot_url}
                              alt=""
                              className="w-11 h-11 rounded-full object-cover bg-bg-secondary shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => slot.player_id && setDetailPlayerId(slot.player_id)}
                              onError={(e) => { e.target.style.display = 'none' }}
                            />
                          )}
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => slot.player_id && setDetailPlayerId(slot.player_id)}
                          >
                            <div className="text-sm font-bold text-text-primary truncate flex items-center gap-1">
                              <span className="truncate hover:text-accent transition-colors">{slot.player_name}</span>
                              <InjuryBadge status={slot.injury_status} />
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-text-primary truncate">
                              {statLine && <span className="truncate">{statLine}</span>}
                              {gameClockLabel(slot) && (
                                <span className={`shrink-0 ${slot.game_status === 'live' ? 'text-accent' : 'text-text-muted'}`}>
                                  {statLine ? '· ' : ''}{gameClockLabel(slot)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end shrink-0">
                            <span className={`text-sm font-display font-bold ${slot.points > 0 ? 'text-white' : 'text-text-muted'}`}>
                              {slot.points > 0 ? slot.points.toFixed(1) : '—'}
                            </span>
                            {slot.projected != null && slot.game_status !== 'final' && (
                              <span className="text-[10px] text-text-primary/60">/ {slot.projected.toFixed(1)}</span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  {/* Away roster */}
                  <div>
                    {matchup.away_roster.filter((s) => H2H_STARTER_SET.has(s.slot)).map((slot) => {
                      const statLine = buildStatLine(slot.stats, slot.position)
                      const slotBorder = slot.game_status === 'live' ? 'border-l-accent' : slot.game_status === 'final' ? 'border-l-correct' : 'border-l-text-primary/20'
                      return (
                        <div key={slot.slot} className={`flex items-center gap-3 px-4 py-3 border-b border-text-primary/5 border-l-2 ${slotBorder}`}>
                          <span className="text-xs font-bold text-text-muted w-7 shrink-0">{H2H_SLOT_LABELS[slot.slot] || slot.slot.toUpperCase()}</span>
                          {slot.headshot_url && (
                            <img
                              src={slot.headshot_url}
                              alt=""
                              className="w-11 h-11 rounded-full object-cover bg-bg-secondary shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => slot.player_id && setDetailPlayerId(slot.player_id)}
                              onError={(e) => { e.target.style.display = 'none' }}
                            />
                          )}
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => slot.player_id && setDetailPlayerId(slot.player_id)}
                          >
                            <div className="text-sm font-bold text-text-primary truncate flex items-center gap-1">
                              <span className="truncate hover:text-accent transition-colors">{slot.player_name}</span>
                              <InjuryBadge status={slot.injury_status} />
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-text-primary truncate">
                              {statLine && <span className="truncate">{statLine}</span>}
                              {gameClockLabel(slot) && (
                                <span className={`shrink-0 ${slot.game_status === 'live' ? 'text-accent' : 'text-text-muted'}`}>
                                  {statLine ? '· ' : ''}{gameClockLabel(slot)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end shrink-0">
                            <span className={`text-sm font-display font-bold ${slot.points > 0 ? 'text-white' : 'text-text-muted'}`}>
                              {slot.points > 0 ? slot.points.toFixed(1) : '—'}
                            </span>
                            {slot.projected != null && slot.game_status !== 'final' && (
                              <span className="text-[10px] text-text-primary/60">/ {slot.projected.toFixed(1)}</span>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}
      {detailPlayerId && (
        <PlayerDetailModal leagueId={league.id} playerId={detailPlayerId} onClose={() => setDetailPlayerId(null)} />
      )}
    </div>
  )
}

export default function FantasyLiveView({ league, fantasySettings }) {
  const isSalaryCap = fantasySettings?.format === 'salary_cap'
  const season = fantasySettings?.season || 2026
  // current_week first. This was `single_week || 1`, and single_week is null in
  // every league that exists — so it evaluated to a hard 1 and the Live tab
  // would have gone on showing Week 1 all season, for salary cap and
  // traditional alike, while the rest of the app moved on. current_week is
  // what the nightly rollover advances; single_week stays as the fallback for
  // single-week leagues, which pin to one week by design.
  const week = fantasySettings?.current_week || fantasySettings?.single_week || 1

  if (isSalaryCap) {
    return <SalaryCapLive league={league} week={week} season={season} />
  }

  return <MatchupLive league={league} week={week} season={season} fantasySettings={fantasySettings} />
}
